// Picture-in-Picture utilities for floating timer display

let pipWindow: Window | null = null;
let pipVideo: HTMLVideoElement | null = null;
let pipCanvas: HTMLCanvasElement | null = null;
let pipAnimationFrame: number | null = null;
let currentTimerState: PiPTimerState | null = null;
let baseElapsedMs: number = 0; // Base elapsed time from last update
let lastUpdateTime: number = 0; // When we last received an update
let baseBreakRemaining: number = 0; // Base break time remaining from last update

export interface PiPTimerState {
  elapsedMs: number;
  projectName: string;
  clientName?: string;
  isRunning: boolean;
  isInterrupt?: boolean;
  interruptSecondsLeft?: number;
  isBudgetWarning?: boolean;
  isBudgetOverrun?: boolean;
  projectColor?: string;
  isBreakTimer?: boolean;
  breakTimeRemaining?: number;
  currentTime?: number;  // Discretized time from main timer for sync
  error?: string;  // Error message if timer crashed
}

/**
 * Check if Picture-in-Picture is supported by the browser
 */
export function isPiPSupported(): boolean {
  return (
    document.pictureInPictureEnabled &&
    'requestPictureInPicture' in HTMLVideoElement.prototype
  );
}

/**
 * Format milliseconds to HH:MM:SS or MM:SS
 */
function formatTime(ms: number): string {
  // Ensure non-negative value
  const safeMs = Math.max(0, ms);
  const totalSeconds = Math.floor(safeMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Draw timer state to canvas
 */
function drawTimerToCanvas(ctx: CanvasRenderingContext2D, state: PiPTimerState) {
  const canvas = ctx.canvas;
  const width = canvas.width;
  const height = canvas.height;

  // Clear canvas
  ctx.clearRect(0, 0, width, height);

  // Determine background color based on state
  let bgColor = '#1a1a1a'; // dark background
  if (state.isBreakTimer) {
    bgColor = '#10b981'; // green for break timer
  } else if (state.isInterrupt) {
    bgColor = '#dc2626'; // red for interrupt
  } else if (state.isBudgetOverrun) {
    bgColor = '#dc2626'; // red for overrun
  } else if (state.isBudgetWarning) {
    bgColor = '#f59e0b'; // amber for warning
  } else if (state.projectColor) {
    bgColor = state.projectColor;
  }

  // Draw background
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, width, height);

  // Draw timer text
  // Use break time remaining if in break mode, otherwise use elapsed time
  const timeStr = state.isBreakTimer && state.breakTimeRemaining !== undefined
    ? formatTime(state.breakTimeRemaining)
    : formatTime(state.elapsedMs);

  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 120px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(timeStr, width / 2, height / 2 - 30);

  // Draw project name
  ctx.font = 'bold 28px sans-serif';
  ctx.fillText(state.projectName, width / 2, height / 2 + 60);

  // Draw client name if available
  if (state.clientName) {
    ctx.font = '20px sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.fillText(`– ${state.clientName}`, width / 2, height / 2 + 90);
  }

  // Draw status text
  if (state.error) {
    // Timer crashed - show error
    ctx.font = 'bold 28px sans-serif';
    ctx.fillStyle = '#ff4444';
    ctx.fillText('⚠️ Timer Crashed', width / 2, height / 2 + 130);
    ctx.font = '18px sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillText('Please restart timer', width / 2, height / 2 + 165);
  } else if (state.isBreakTimer) {
    ctx.font = 'bold 28px sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText('☕ Break Time!', width / 2, height / 2 + 130);
  } else if (state.isInterrupt && state.interruptSecondsLeft !== undefined) {
    ctx.font = 'bold 24px sans-serif';
    ctx.fillStyle = '#ffffff';
    const minutes = Math.floor(state.interruptSecondsLeft / 60);
    const seconds = state.interruptSecondsLeft % 60;
    const countdown = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    ctx.fillText(`⚠️ Respond in ${countdown}`, width / 2, height / 2 + 130);
  } else if (state.isBudgetOverrun) {
    ctx.font = 'bold 24px sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText('⚠️ Budget Exceeded', width / 2, height / 2 + 130);
  } else if (state.isBudgetWarning) {
    ctx.font = 'bold 24px sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText('⚠️ Approaching Budget Limit', width / 2, height / 2 + 130);
  } else if (!state.isRunning) {
    ctx.font = '20px sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.fillText('Timer Stopped', width / 2, height / 2 + 130);
  }

  // Draw iTimedIT branding
  ctx.font = '16px sans-serif';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.fillText('iTimedIT', width / 2, height - 20);
}

/**
 * Animation loop to update canvas continuously
 */
function animatePiP() {
  if (!pipCanvas || !currentTimerState) {
    return;
  }

  const ctx = pipCanvas.getContext('2d');
  if (!ctx) return;

  // Calculate real-time elapsed time if timer is running
  let displayState = { ...currentTimerState };
  // Use discretized currentTime if available, otherwise fall back to Date.now()
  const currentTime = currentTimerState.currentTime || Date.now();
  const timeSinceUpdate = currentTime - lastUpdateTime;

  // Only update elapsed/remaining time if timer is actually running
  if (currentTimerState.isRunning) {
    if (!currentTimerState.isBreakTimer) {
      // Work timer: add time since last update (ensure non-negative)
      displayState.elapsedMs = Math.max(0, baseElapsedMs + timeSinceUpdate);
    } else {
      // Break timer: subtract time since last update from base remaining
      displayState.breakTimeRemaining = Math.max(0, baseBreakRemaining - timeSinceUpdate);
    }
  } else {
    // Timer stopped: freeze the time at base values
    displayState.elapsedMs = baseElapsedMs;
    displayState.breakTimeRemaining = baseBreakRemaining;
  }

  drawTimerToCanvas(ctx, displayState);

  // Schedule next frame
  pipAnimationFrame = requestAnimationFrame(animatePiP);
}

/**
 * Open Picture-in-Picture window with timer
 */
export async function openPiPTimer(initialState: PiPTimerState): Promise<boolean> {
  if (!isPiPSupported()) {
    console.warn('Picture-in-Picture is not supported');
    return false;
  }

  try {
    // Create canvas element
    pipCanvas = document.createElement('canvas');
    pipCanvas.width = 640;
    pipCanvas.height = 360;

    // Draw initial state
    const ctx = pipCanvas.getContext('2d');
    if (!ctx) {
      console.error('Failed to get canvas context');
      return false;
    }
    drawTimerToCanvas(ctx, initialState);

    // Create video element
    pipVideo = document.createElement('video');
    pipVideo.muted = true;
    pipVideo.playsInline = true;
    pipVideo.autoplay = true;

    // Capture canvas stream
    const stream = pipCanvas.captureStream(15); // 15 FPS - sufficient for timer display
    pipVideo.srcObject = stream;

    // Wait for video to be ready
    await pipVideo.play();

    // Request Picture-in-Picture
    pipWindow = await pipVideo.requestPictureInPicture();

    // Handle PiP close
    pipVideo.addEventListener('leavepictureinpicture', () => {
      closePiPTimer();
    });

    // Store initial state and start animation loop
    currentTimerState = initialState;
    baseElapsedMs = initialState.elapsedMs;
    baseBreakRemaining = initialState.breakTimeRemaining || 0;
    lastUpdateTime = Date.now();
    animatePiP(); // Start continuous animation

    console.log('Picture-in-Picture timer opened');
    return true;
  } catch (error) {
    console.error('Failed to open Picture-in-Picture:', error);
    closePiPTimer();
    return false;
  }
}

/**
 * Update timer state in PiP window
 */
export function updatePiPTimer(state: PiPTimerState): void {
  if (!pipCanvas || !pipWindow) {
    return;
  }

  // ALWAYS update currentTimerState first, then decide if we need to resync base values
  const wasRunning = currentTimerState?.isRunning ?? false;
  const previousState = currentTimerState;

  // Always update the current state for properties
  currentTimerState = state;

  // Check if the state has meaningfully changed
  const stateChanged =
    !previousState ||
    previousState.isRunning !== state.isRunning ||
    previousState.isBreakTimer !== state.isBreakTimer ||
    previousState.projectName !== state.projectName ||
    previousState.isInterrupt !== state.isInterrupt;

  // Calculate time since last update for periodic resyncs
  const timeSinceLastUpdate = Date.now() - lastUpdateTime;
  const periodicResync = state.isRunning && timeSinceLastUpdate > 5000; // Only periodic resync when running

  // For work timers, check if elapsed time has drifted significantly
  const needsWorkResync = !state.isBreakTimer &&
    previousState &&
    state.isRunning && // Only check drift when timer is running
    Math.abs((baseElapsedMs + (Date.now() - lastUpdateTime)) - state.elapsedMs) > 100;

  // For break timers, check if remaining time has drifted significantly
  // Also resync if we just started a break (baseBreakRemaining is 0 but state has break time)
  const needsBreakResync = state.isBreakTimer &&
    state.breakTimeRemaining !== undefined &&
    state.isRunning && // Only check drift when timer is running
    (baseBreakRemaining === 0 || // First break update
     Math.abs((baseBreakRemaining - (Date.now() - lastUpdateTime)) - state.breakTimeRemaining) > 100);

  // CRITICAL: Always update base values when timer stops or starts
  // Also update on state changes, periodic resync, or significant drift
  const shouldUpdate = stateChanged || periodicResync || needsWorkResync || needsBreakResync;

  if (shouldUpdate) {
    baseElapsedMs = state.elapsedMs;
    baseBreakRemaining = state.breakTimeRemaining || 0;
    lastUpdateTime = Date.now();
  }

  // The animation loop will pick up the new state automatically
}

/**
 * Close Picture-in-Picture window
 */
export function closePiPTimer(): void {
  if (pipAnimationFrame !== null) {
    cancelAnimationFrame(pipAnimationFrame);
    pipAnimationFrame = null;
  }

  if (document.pictureInPictureElement) {
    document.exitPictureInPicture().catch(console.error);
  }

  if (pipVideo) {
    pipVideo.srcObject = null;
    pipVideo = null;
  }

  pipCanvas = null;
  pipWindow = null;
  currentTimerState = null;
  baseElapsedMs = 0;
  lastUpdateTime = 0;
  baseBreakRemaining = 0;

  console.log('Picture-in-Picture timer closed');
}

/**
 * Check if PiP is currently active
 */
export function isPiPActive(): boolean {
  return pipWindow !== null && document.pictureInPictureElement !== null;
}
